import React, { useEffect, useRef, useState } from "react";
import { inject, observer } from "mobx-react";
import { Helmet } from "react-helmet";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import UiFormTitle from "../../common/UiFormTitle";
import LabelAutocomplete, {
  fetchImagesCountByLabels,
} from "./LabelAutocomplete";
import LabelSummary from "./LabelSummary";
import ImportProjectSummary from "./ImportProjectSummary";
import ProjectFilter from "./ProjectFilter";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import UiButton from "pages/common/UiButton";
import withContext from "../../../tools/withContext";
import ImportLabelsStore, {
  ProjectOption,
} from "store/import-labels-store/importLabelsStore";

const ImportLabelsForm = (props: any) => {
  const { classes, project, importLabelsStore, ctx } = props;
  const { appFetch } = ctx;
  const store = importLabelsStore as ImportLabelsStore;
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [previewMode, setPreviewMode] = useState(true);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handlePreviewNow = async () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    store.setPreviewLoading(true);
    store.setPreviewResult(null);
    try {
      const result = await fetchImagesCountByLabels(
        appFetch,
        store.labels,
        store.projects.map((p) => p.value),
        project.client.id,
        project.labelingTypes?.[0]?.name,
      );
      store.setPreviewResult(result);
    } catch {
      store.setPreviewResult(null);
    } finally {
      if (store.previewResult !== null && store.previewResult.total > 0) {
        setPreviewMode(false);
      }
      store.setPreviewLoading(false);
    }
  };

  return (
    <Grid
      container
      direction="row"
      justify="center"
      alignItems="flex-start"
      className={classes.root}
    >
      <Helmet>
        <title>Importer des images</title>
      </Helmet>
      <Grid item xs={12} sm={10} md={8} xl={7}>
        <Paper className={classes.paper}>
          <UiFormTitle>{`1. Importer les images`}</UiFormTitle>
          <form id="form">
            <Grid
              container
              spacing={16}
              className={classes.form}
              style={{ paddingBottom: 16 }}
            >
              <Grid item xs={12} style={{ paddingLeft: 0, paddingRight: 0 }}>
                <ImportProjectSummary project={project} />
                <Grid container spacing={16} alignItems="flex-end" style={{ marginTop: 12 }}>
                  <Grid item xs={12} sm={12} md={7} lg={7}>
                    <LabelAutocomplete
                      keepInputValue={true}
                      value={store.labels}
                      onChange={(values) => {
                        store.setLabels(values);
                        setPreviewMode(true);
                      }}
                      appFetch={appFetch}
                      clientId={project.client.id}
                      labelingType={project.labelingTypes?.[0]?.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={5} lg={5}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-end",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <ProjectFilter
                          required
                          value={store.projects}
                          onChange={(values: ProjectOption[]) => {
                            store.setProjects(values);
                            setPreviewMode(true);
                          }}
                          clientId={project.client.id}
                          labelingType={project.labelingTypes?.[0]?.name}
                        />
                      </div>
                    </div>
                  </Grid>
                </Grid>
                <LabelSummary
                  result={store.previewResult}
                  isLoading={store.previewLoading}
                  showImagePreview
                  previewDialogClasses={{
                    filePreviewDialogTitle: classes.filePreviewDialogTitle,
                    filePreviewDialogTitleText: classes.filePreviewDialogTitleText,
                    filePreviewDialogCloseButton: classes.filePreviewDialogCloseButton,
                    filePreviewImage: classes.filePreviewImage,
                  }}
                />
              </Grid>
            </Grid>
          </form>
          <Grid
            item
            container
            justify="flex-end"
            xs={12}
            style={{ padding: "20px" }}
          >
            {previewMode ? (
              <UiButton
                variant="outlined"
                color="primary"
                onClick={handlePreviewNow}
                disabled={
                  store.previewLoading || store.projects.length === 0
                }
                style={{ height: 42, whiteSpace: "nowrap" }}
              >
                Prévisualiser
              </UiButton>
            ) : (
              store.previewResult !== null && (
                <UiButton
                  color={"primary"}
                  variant="contained"
                  onClick={() => store.setStep(2)}
                  style={{ height: "42px" }}
                  disabled={store.previewLoading}
                >
                  Valider et générer des lots
                  <NavigateNextIcon style={{ marginLeft: 8 }} />
                </UiButton>
              )
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default withContext(
  inject("importLabelsStore")(observer(ImportLabelsForm)),
);
